import React, { useEffect, useState } from 'react';
import { BOAPI } from "../service/UsersService";

const EXT_LABEL = { jpg: 'תמונה', jpeg: 'תמונה', png: 'תמונה', video: 'וידאו', youtube: 'וידאו', pdf: 'PDF', text: 'טקסט', decision: 'החלטה', quiz: 'מבחן' };

// ── "היום"/"אתמול"/תאריך+שעה — אותה מוסכמה כמו ActivitiesTab.jsx, כדי
// שהערות ישנות עדיין יראו תאריך ברור ולא רק שעה. ──
const formatFeedbackTime = (iTime) => {
  const s = iTime || '';
  if (!s) return '';
  const [datePart, timePart] = s.split(/[T ]/);
  const [y, m, d] = datePart.split('-');
  const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  const time = (timePart || '').substring(0, 5);
  if (dateObj.toDateString() === today.toDateString()) return `היום ${time}`;
  if (dateObj.toDateString() === yesterday.toDateString()) return `אתמול ${time}`;
  return `${d}/${m} ${time}`;
};

const DashboardTab = () => {
  const [tasks, setTasks]   = useState([]);
  const [stories, setStories] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [feedbackView, setFeedbackView] = useState('pending'); // pending | done
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const [digestEmail, setDigestEmail] = useState('');
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestMsg, setDigestMsg] = useState(null);

  const loadFeedback = async (view) => {
    setFeedbackLoading(true);
    try {
      const res = await BOAPI.fetchUserFeedback(view === 'done');
      if (res?.[0]?.[0]?.code === 0) setFeedback(res[1] || []);
      else setError(res?.[0]?.[0]?.msg || 'שגיאה בטעינת ההערות');
    } catch (e) { setError(e.message); }
    finally { setFeedbackLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const statsRes = await BOAPI.fetchTaskStats();
      if (statsRes?.[0]?.[0]?.code === 0) {
        setTasks(statsRes[1] || []);
        setStories(statsRes[2] || []);
      } else {
        setError(statsRes?.[0]?.[0]?.msg || 'שגיאה בטעינת הנתונים');
      }
      await loadFeedback(feedbackView);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ── מעבר בין "לא טופלו"/"טופלו" שולף מחדש מהשרת (לא רק סינון מקומי) —
  // כדי לתמוך בטווח "טופלו" שמעולם לא נטען אצל הקליינט. ──
  useEffect(() => {
    if (loading) return; // הטעינה הראשונית כבר קוראת ל-loadFeedback בעצמה
    loadFeedback(feedbackView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackView]);

  // ── "בוצע"/"החזרה ללא טופלה" — isActive בשרת (לא מחיקה), ומסירים מקומית
  // מיד כדי שהרשימה תעודכן בלי לחכות לטעינה נוספת (אחרי toggle הפריט עובר
  // לרשימה השנייה, אז הוא כבר לא שייך לתצוגה הנוכחית). ──
  const handleToggleFeedback = async (id, done) => {
    const prev = feedback;
    setFeedback((list) => list.filter((f) => f.id !== id));
    try {
      const res = await BOAPI.markFeedbackDone(id, done);
      if (res?.[0]?.[0]?.code !== 0) {
        setFeedback(prev);
        setError(res?.[0]?.[0]?.msg || 'שגיאה בעדכון ההערה');
      }
    } catch (e) {
      setFeedback(prev);
      setError(e.message);
    }
  };

  const handleSendDigest = async () => {
    if (!digestEmail || !digestEmail.includes('@')) {
      setDigestMsg({ type: 'error', text: 'יש להזין כתובת מייל תקינה' });
      return;
    }
    setSendingDigest(true);
    setDigestMsg(null);
    try {
      const res = await BOAPI.sendDailyDigestEmail(digestEmail);
      if (res?.[0]?.[0]?.code === 0) {
        setDigestMsg({ type: 'success', text: `✅ הסיכום נשלח ל-${digestEmail}` });
      } else {
        setDigestMsg({ type: 'error', text: res?.[0]?.[0]?.msg || 'שגיאה בשליחת המייל' });
      }
    } catch (e) {
      setDigestMsg({ type: 'error', text: e.message });
    } finally {
      setSendingDigest(false);
    }
  };

  useEffect(() => { load(); }, []);

  const maxUsers = tasks.length > 0 ? Math.max(...tasks.map(t => t.userCount || 0)) : 1;
  const maxViews = stories.length > 0 ? Math.max(...stories.map(t => t.views || 0)) : 1;

  if (loading) return <div style={s.center}>טוען דשבורד...</div>;

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>דשבורד פעילות</h2>
        <button style={s.refreshBtn} onClick={load}>🔄 רענן</button>
      </div>

      <div style={s.todayBadge}>
        📅 נתוני היום — {new Date().toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' })}
      </div>

      <div style={s.digestBox}>
        <span style={s.digestLabel}>📧 שליחת סיכום היום (מתחילת היום ועד עכשיו) לכתובת מייל:</span>
        <div style={s.digestRow}>
          <input
            type="email"
            placeholder="example@mail.com"
            value={digestEmail}
            onChange={(e) => setDigestEmail(e.target.value)}
            style={s.digestInput}
          />
          <button style={s.digestBtn} onClick={handleSendDigest} disabled={sendingDigest}>
            {sendingDigest ? 'שולח...' : '📨 שלח סיכום'}
          </button>
        </div>
        {digestMsg && (
          <div style={digestMsg.type === 'error' ? s.digestMsgError : s.digestMsgSuccess}>
            {digestMsg.text}
          </div>
        )}
      </div>

      {error && (
        <div style={s.errorBanner}>
          ❌ {error}
          <button style={s.closeErr} onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div style={s.twoCol}>

        {/* ── משימות היום ── */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <span style={s.sectionTitle}>📋 משימות — כניסות היום</span>
            <span style={s.sectionCount}>{tasks.length} שיעורים פעילים</span>
          </div>

          {tasks.length === 0 ? (
            <div style={s.empty}>אין נתוני משימות להיום עדיין</div>
          ) : (
            <div style={s.taskList}>
              {tasks.map((t, idx) => {
                const pct = Math.round(((t.userCount || 0) / maxUsers) * 100);
                const completePct = t.completedCount && t.userCount
                  ? Math.round((t.completedCount / t.userCount) * 100)
                  : 0;
                return (
                  <div key={t.taskId || idx} style={s.taskRow}>
                    <div style={s.taskMeta}>
                      <div style={s.taskName}>{t.taskName}</div>
                      <div style={s.taskStats}>
                        <span style={s.statPill}>{t.userCount || 0} 👤</span>
                        <span style={{ ...s.statPill, background: '#e8f5e9', color: '#2e7d32' }}>
                          {t.completedCount || 0} ✅
                        </span>
                        {t.avgProgress !== null && (
                          <span style={{ ...s.statPill, background: '#fff3e0', color: '#e65100' }}>
                            ממוצע: {Math.round(t.avgProgress || 0)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={s.barTrack}>
                      <div style={{ ...s.barFill, width: `${pct}%`, background: completePct >= 70 ? '#34d399' : '#1f6feb' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── סטוריז — כל הזמן ── */}
        <div style={s.section}>
          <div style={s.sectionHeader}>
            <span style={s.sectionTitle}>📸 סטוריז — צפיות (כל הזמן)</span>
            <span style={s.sectionCount}>{stories.length} סטוריז</span>
          </div>

          {stories.length === 0 ? (
            <div style={s.empty}>אין סטוריז פעילים</div>
          ) : (
            <div style={s.taskList}>
              {stories.map((s2, idx) => {
                const pct = Math.round(((s2.views || 0) / maxViews) * 100);
                const extLabel = EXT_LABEL[s2.ext] || s2.ext;
                return (
                  <div key={s2.storyId || idx} style={s.taskRow}>
                    <div style={s.taskMeta}>
                      <div style={s.taskName}>{s2.storyName}</div>
                      <div style={s.taskStats}>
                        <span style={s.statPill}>{s2.views || 0} 👁</span>
                        <span style={{ ...s.statPill, background: '#f3e5f5', color: '#7b1fa2' }}>{extLabel}</span>
                      </div>
                    </div>
                    <div style={s.barTrack}>
                      <div style={{ ...s.barFill, width: `${pct}%`, background: '#a78bfa' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* ── הערות/הארות משתמשים על משימות ("הערה" ב-TaskRow.tsx) — לצורכי ביקורת ── */}
      <div style={{ ...s.section, marginTop: 24 }}>
        <div style={s.sectionHeader}>
          <span style={s.sectionTitle}>💬 הערות משתמשים על משימות</span>
          <div style={s.feedbackViewToggle}>
            <button
              style={{ ...s.feedbackViewBtn, ...(feedbackView === 'pending' ? s.feedbackViewBtnActive : {}) }}
              onClick={() => setFeedbackView('pending')}
            >
              לא טופלו
            </button>
            <button
              style={{ ...s.feedbackViewBtn, ...(feedbackView === 'done' ? s.feedbackViewBtnActive : {}) }}
              onClick={() => setFeedbackView('done')}
            >
              טופלו
            </button>
          </div>
          <span style={s.sectionCount}>{feedback.length} הערות</span>
        </div>

        {feedbackLoading ? (
          <div style={s.empty}>טוען...</div>
        ) : feedback.length === 0 ? (
          <div style={s.empty}>{feedbackView === 'pending' ? 'אין הערות שממתינות לטיפול' : 'אין הערות שטופלו עדיין'}</div>
        ) : (
          <div style={s.feedbackList}>
            {feedback.map((f) => (
              <div key={f.id} style={s.feedbackCard}>
                <div style={s.feedbackTop}>
                  <span style={s.feedbackUser}>👤 {f.userName || 'ללא שם'}</span>
                  <div style={s.feedbackTopRight}>
                    <span style={s.feedbackTime}>{formatFeedbackTime(f.iTime)}</span>
                    {feedbackView === 'pending' ? (
                      <button style={s.feedbackDoneBtn} onClick={() => handleToggleFeedback(f.id, true)}>✓ בוצע</button>
                    ) : (
                      <button style={s.feedbackUndoBtn} onClick={() => handleToggleFeedback(f.id, false)}>↩ החזר ללא טופלה</button>
                    )}
                  </div>
                </div>
                {f.taskName && <div style={s.feedbackTask}>📋 {f.taskName}</div>}
                <div style={s.feedbackContent}>{f.content}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 8 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  refreshBtn:   { padding: '8px 16px', background: '#fff', border: '1.5px solid #e3e8ee', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  todayBadge:   { display: 'inline-block', background: '#e8f5e9', color: '#2e7d32', borderRadius: 20, padding: '5px 16px', fontSize: 13, fontWeight: 700, marginBottom: 20 },

  digestBox:    { background: '#fff', border: '1px solid #e3e8ee', borderRadius: 12, padding: 16, marginBottom: 20 },
  digestLabel:  { fontSize: 13, fontWeight: 600, color: '#1a1f36' },
  digestRow:    { display: 'flex', gap: 8, marginTop: 10 },
  digestInput:  { flex: 1, padding: '8px 12px', border: '1.5px solid #e3e8ee', borderRadius: 8, fontSize: 14, direction: 'ltr', textAlign: 'right' },
  digestBtn:    { padding: '8px 18px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  digestMsgSuccess: { marginTop: 10, color: '#2e7d32', fontSize: 13, fontWeight: 600 },
  digestMsgError:   { marginTop: 10, color: '#c62828', fontSize: 13, fontWeight: 600 },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },

  twoCol:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 },
  section:      { background: '#fff', borderRadius: 16, border: '1px solid #e3e8ee', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', padding: 22 },
  sectionHeader:{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 14, borderBottom: '2px solid #f4f6f8' },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: '#1a1f36' },
  sectionCount: { background: '#e8eaf6', color: '#1f6feb', borderRadius: 12, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  empty:        { color: '#aaa', textAlign: 'center', padding: '30px 0', fontSize: 14 },

  taskList:     { display: 'flex', flexDirection: 'column', gap: 14 },
  taskRow:      {},
  taskMeta:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6, flexWrap: 'wrap', gap: 6 },
  taskName:     { fontSize: 14, fontWeight: 600, color: '#1a1f36', flex: 1 },
  taskStats:    { display: 'flex', gap: 6, flexWrap: 'wrap' },
  statPill:     { background: '#e8eaf6', color: '#1f6feb', borderRadius: 20, padding: '2px 9px', fontSize: 12, fontWeight: 600 },
  barTrack:     { height: 6, background: '#f0f2f5', borderRadius: 3, overflow: 'hidden' },
  barFill:      { height: '100%', borderRadius: 3, transition: 'width 0.4s ease' },

  feedbackList:    { display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 480, overflowY: 'auto' },
  feedbackCard:    { background: '#fafbfc', border: '1px solid #eef1f4', borderRadius: 12, padding: '12px 14px' },
  feedbackTop:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  feedbackUser:    { fontSize: 13, fontWeight: 700, color: '#1a1f36' },
  feedbackTopRight:{ display: 'flex', alignItems: 'center', gap: 10 },
  feedbackTime:    { fontSize: 12, color: '#9aa4b2' },
  feedbackDoneBtn: { padding: '4px 10px', background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  feedbackUndoBtn: { padding: '4px 10px', background: '#fff3e0', color: '#e65100', border: '1px solid #ffe0b2', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  feedbackViewToggle: { display: 'flex', gap: 6 },
  feedbackViewBtn: { padding: '6px 14px', background: '#f4f6f8', color: '#697386', border: 'none', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  feedbackViewBtnActive: { background: '#1f6feb', color: '#fff' },
  feedbackTask:    { fontSize: 12, color: '#1f6feb', fontWeight: 600, marginBottom: 6 },
  feedbackContent: { fontSize: 14, color: '#33404f', lineHeight: 1.5, whiteSpace: 'pre-wrap' },
};

export default DashboardTab;
